import { defineMessages } from '../../i18n/messages';

export const ProofDifferentiationMessages = defineMessages({
  TITLE: {
    id: 'homepage.proof.title',
    text: 'Why Developers Choose Localingos',
    description: 'Section heading for the competitive differentiation section. Explains why Localingos is better than alternatives.',
  },
  SUBTITLE: {
    id: 'homepage.proof.subtitle',
    text: 'Google Translate cannot integrate into your codebase. Traditional services cost thousands and take weeks. Localingos does both, integration and translation, automatically.',
    description: 'Subtitle that contrasts Localingos with two alternatives: Google Translate (no code integration) and traditional translation agencies (expensive and slow). "Codebase" means the source code of a software project.',
  },
  POINT_MINUTES_TITLE: {
    id: 'homepage.proof.point_minutes_title',
    text: 'Zero Integration Effort',
    description: 'Title for a proof point card about automated integration. Means developers do not need to manually set up internationalization.',
  },
  POINT_MINUTES_DESC: {
    id: 'homepage.proof.point_minutes_desc',
    text: 'No researching i18n libraries, no refactoring strings, no wiring locale files. Our CLI detects your tech stack and generates a prompt that lets your AI agent handle the entire migration.',
    description: 'Description for the integration proof point. Lists things developers do NOT need to do. "i18n" is internationalization. "Refactoring strings" means extracting hardcoded text into translation keys. "Locale files" are translation files.',
  },
  POINT_PAYGO_TITLE: {
    id: 'homepage.proof.point_paygo_title',
    text: 'A Pipeline, Not a Prompt',
    description: 'Title for a proof point card. Contrasts a managed translation pipeline with a one-off prompt to an AI chat assistant. "Prompt" is the instruction text sent to an AI model.',
  },
  POINT_PAYGO_DESC: {
    id: 'homepage.proof.point_paygo_desc',
    text: 'Translation is the easy part. We add placeholder validation, corrective retries and incremental sync, so what lands in your repo is checked rather than merely generated — and because it is automated end to end, it costs a fraction of a human translation service.',
    description: 'Description for the pipeline proof point. "Placeholder validation" means verifying code variables survived translation. "Corrective retries" means asking the model again with feedback when output is wrong. "Incremental sync" means only changed strings are re-translated. "Repo" is short for source code repository.',
  },
  POINT_VARIABLE_SAFE_TITLE: {
    id: 'homepage.proof.point_variable_safe_title',
    text: 'Variable-Safe',
    description: 'Title for a proof point card about placeholder preservation. "Variable-safe" means code variables inside translatable strings are not corrupted.',
  },
  POINT_VARIABLE_SAFE_DESC: {
    id: 'homepage.proof.point_variable_safe_desc',
    // eslint-disable-next-line no-template-curly-in-string
    text: 'We preserve ${variables}, %s, {count}, and placeholders correctly during translation.',
    // eslint-disable-next-line no-template-curly-in-string
    description: 'Description for the variable-safe proof point. "${variables}", "%s", and "{count}" are code placeholder examples. Do NOT translate these placeholder examples.',
  },
  POINT_SCALES_TITLE: {
    id: 'homepage.proof.point_scales_title',
    text: 'Scales on Demand',
    description: 'Title for a proof point card about throughput capacity. Means the system can handle large volumes of translations.',
  },
  POINT_SCALES_DESC: {
    id: 'homepage.proof.point_scales_desc',
    text: '1,000+ translations/min capacity. Perfect for bulk jobs and high-volume workflows.',
    description: 'Description for the scaling proof point. "1,000+" means more than one thousand. "Bulk jobs" means large batches of translation work.',
  },
  COMPETITORS_TITLE: {
    id: 'homepage.proof.competitors_title',
    text: 'Traditional Competitors',
    description: 'Column heading in a comparison table showing drawbacks of traditional translation services.',
  },
  COMPETITORS_HUMAN_SLOW: {
    id: 'homepage.proof.competitors_human_slow',
    text: 'Manual i18n setup (days of refactoring)',
    description: 'Competitor drawback bullet. "i18n" is internationalization. "Refactoring" means restructuring code. This takes days with traditional approaches.',
  },
  COMPETITORS_DAYS_WEEKS: {
    id: 'homepage.proof.competitors_days_weeks',
    text: 'Human translators (slow & expensive)',
    description: 'Competitor drawback bullet. Traditional services use human translators which are slower and more costly.',
  },
  COMPETITORS_MONTHLY_FEES: {
    id: 'homepage.proof.competitors_monthly_fees',
    text: 'Per-seat pricing ($120-400+/month)',
    description: 'Competitor drawback bullet. Traditional services charge per user. "$120-400+" is a price range in US dollars.',
  },
  COMPETITORS_PLACEHOLDER_RISK: {
    id: 'homepage.proof.competitors_placeholder_risk',
    text: 'No codebase integration, just raw text translation',
    description: 'Competitor drawback bullet. Traditional services only translate text without integrating into the software project.',
  },
  LOCALINGOS_TITLE: {
    id: 'homepage.proof.localingos_title',
    text: 'Localingos',
    description: 'Column heading in a comparison table showing advantages of Localingos. "Localingos" is the product name, do not translate.',
  },
  LOCALINGOS_LLM_AUTOMATION: {
    id: 'homepage.proof.localingos_llm_automation',
    text: 'AI-powered i18n migration in minutes',
    description: 'Localingos advantage bullet. "i18n migration" means converting a codebase to support multiple languages. This is done by AI in minutes.',
  },
  LOCALINGOS_MINUTES_DELIVERY: {
    id: 'homepage.proof.localingos_minutes_delivery',
    text: 'Validated AI translations in minutes, not weeks',
    description: 'Localingos advantage bullet, shown opposite a competitor row about slow human translators. "Validated" means every translation is checked for placeholder integrity before it is returned.',
  },
  LOCALINGOS_PAY_USAGE: {
    id: 'homepage.proof.localingos_pay_usage',
    text: 'Free forever + plans from $9/mo, unlimited members',
    description: 'Localingos advantage bullet. Communicates the free tier, affordable paid plans, and no per-seat pricing. "$9/mo" means 9 US dollars per month. "Unlimited members" means no charge per team member.',
  },
  LOCALINGOS_GUARANTEED_PLACEHOLDER: {
    id: 'homepage.proof.localingos_guaranteed_placeholder',
    text: 'One CLI command to sync, translate & update',
    description: 'Localingos advantage bullet. Emphasizes simplicity: a single command handles the entire workflow. "CLI" is Command Line Interface.',
  },
});
